import { useTicketGroupEdit } from './TicketGroupEditContext';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import logException from '@dashboard/services/logException';
import { confirm } from '@dashboard/lib/confirm';
import { useTicketGroups } from './TicketGroupsContext';
import { useMutation } from '@tanstack/react-query';
import usePage from '@dashboard/hooks/usePage';

function useTicketGroupHelpers(ticketGroupId) {
  if (!ticketGroupId) {
    throw new Error('ticketGroupId is required');
  }

  const { refetch: refetchPage } = usePage();
  const { token, sellerId } = useAuth();

  const { state: ticketGroups, dispatch } = useTicketGroups();
  const {
    setIsAddingTicketGroup,
    setIsEditingTicketGroup,
    setHasUnsavedChanges,
    hasUnsavedChanges,
    editingTicketGroupId,
  } = useTicketGroupEdit();

  const ticketGroup = ticketGroups.find(ticketGroup => ticketGroup._id === ticketGroupId);

  const totalQuantitySoldInGroup = (ticketGroup.ticketTypes ?? []).reduce(
    (acc, ticket) => acc + (ticket.quantitySold || 0),
    0,
  );

  async function onCancelAddTicketGroup() {
    let proceed = true;

    if (hasUnsavedChanges) {
      proceed = await confirm('Discard unsaved changes?', {
        confirm: 'Discard',
      });
    }

    if (!proceed) return;

    dispatch({
      type: 'delete',
      ticketGroup: ticketGroup,
    });
    setHasUnsavedChanges(false);
    setIsAddingTicketGroup(false);
  }

  const deleteTicketGroupMutation = useMutation({
    mutationFn: async ticketGroupId =>
      fetcher(`/sellers/${sellerId}/ticket-groups/${ticketGroupId}`, token, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      setHasUnsavedChanges(false);
      setIsEditingTicketGroup(false);
      refetchPage();
    },
    onError: error => {
      logException(error);
      window.alert('Failed to delete ticket group. Try again or contact support');
    },
  });

  async function onDeleteTicketGroup() {
    const del = await confirm('Delete ticket group?', {
      confirm: 'Delete',
    });

    if (!del) return;

    dispatch({ type: 'delete', ticketGroup: ticketGroup });

    if (!ticketGroup._id) {
      return;
    }

    deleteTicketGroupMutation.mutate(ticketGroup._id);
  }

  const archiveTicketGroupMutation = useMutation({
    mutationFn: async ticketGroupId =>
      fetcher(`/sellers/${sellerId}/ticket-groups/${ticketGroupId}`, token, {
        method: 'PUT',
        body: JSON.stringify({ status: 'archived' }),
      }),
    onSuccess: () => {
      setHasUnsavedChanges(false);
      refetchPage();
    },
    onError: error => {
      logException(error);
      window.alert('Failed to archive ticket group. Try again or contact support');
    },
  });

  async function onArchiveTicketGroup() {
    const proceed = await confirm('Archive this ticket group?', {
      description:
        "Archived ticket groups don't count towards the event's capacity. If you want to hide the ticket group, you can disable it instead.",
      confirm: 'Archive',
    });

    if (!proceed) return;

    dispatch({
      type: 'update',
      ticketGroupId,
      values: { ...ticketGroup, status: 'archived' },
    });

    if (!ticketGroup._id) {
      return;
    }

    archiveTicketGroupMutation.mutate(ticketGroup._id);
  }

  const duplicateTicketGroupMutation = useMutation({
    mutationFn: () =>
      fetcher(`/sellers/${sellerId}/ticket-groups/${ticketGroupId}/duplicate`, token, {
        method: 'POST',
      }),
    onSuccess: data => {
      const currentIndex = ticketGroups.findIndex(tg => tg._id === ticketGroupId);
      const newIndex = currentIndex + 1;

      dispatch({
        type: 'add',
        values: [data],
        index: newIndex,
      });

      refetchPage();
    },
  });

  async function onDuplicateTicketGroup() {
    await duplicateTicketGroupMutation.mutateAsync();
  }

  const enableDisableTicketGroupMutation = useMutation({
    mutationFn: async ({ status }) =>
      fetcher(`/sellers/${sellerId}/ticket-groups/${ticketGroup._id}`, token, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      }),
    onSuccess: () => {
      setHasUnsavedChanges(false);
      refetchPage();
    },
    onError: error => {
      logException(error);
      window.alert('Failed to enable/disable ticket group. Try again or contact support');
    },
  });

  async function onEnableDisableTicketGroup() {
    const status = ticketGroup.status === 'enabled' ? 'disabled' : 'enabled';

    dispatch({
      type: 'update',
      ticketGroupId,
      values: {
        ...ticketGroup,
        status,
      },
    });

    if (!ticketGroup._id) {
      return;
    }

    enableDisableTicketGroupMutation.mutate({ status });
  }

  function onDirty(dirty) {
    // if there is only 1 ticket group, it is always in edit mode
    if (ticketGroups.length === 1) {
      setHasUnsavedChanges(dirty);
      return;
    } else {
      // if there are multiple ticket groups, only set unsaved changes if the current ticket group is in edit mode
      if (ticketGroup._id !== editingTicketGroupId) return;

      setHasUnsavedChanges(dirty);
    }
  }

  return {
    onCancelAddTicketGroup: onCancelAddTicketGroup,
    onArchiveTicketGroup: onArchiveTicketGroup,
    onDeleteTicketGroup: onDeleteTicketGroup,
    onDuplicateTicketGroup: onDuplicateTicketGroup,
    onEnableDisableTicketGroup: onEnableDisableTicketGroup,
    onDirty,
    totalQuantitySoldInGroup,
  };
}

export { useTicketGroupHelpers };
